import { Controller } from "@hotwired/stimulus";
import {
  dispatchModelingPayload,
  hideModelingLoading,
  showModelingLoading,
} from "../scenario_modeling/payload_refresh";
import { appendModelingControlParams } from "../scenario_modeling/request_params";
import { toggleVolDeclaredEmptyState } from "../scenario_modeling/vol_declared_input";

export default class extends Controller {
  static targets = [
    "facilityCheckbox",
    "selectAllCheckbox",
    "selectedCountBadge",
    "buyingPctCell",
    "volDeclaredInput",
  ];

  static values = {
    panelId: String,
    exchangeRatesUrl: String,
    selectedCountTemplate: { type: String, default: "%{count} Selected" },
    customized: { type: Boolean, default: false },
  };

  connect() {
    this._fetchInFlight = false;
    this._recalcQueued = false;
    this._debounceTimer = null;
    this._syncSelectAll();

    // Paging swaps this table out; save an edit still waiting on the debounce
    // while its input is in the page, or it would be lost.
    this._frame = this.element.closest("turbo-frame");
    this._onBeforeFrameRender = () => this._flushPendingRecalculate();
    this._frame?.addEventListener("turbo:before-frame-render", this._onBeforeFrameRender);
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._frame?.removeEventListener("turbo:before-frame-render", this._onBeforeFrameRender);
  }

  _flushPendingRecalculate() {
    if (!this._debounceTimer) return;

    clearTimeout(this._debounceTimer);
    this._debounceTimer = null;
    this._recalculate();
  }

  onFacilityChange(event) {
    const checked = event.target.checked;
    const facilityId = event.target.value;
    this.facilityCheckboxTargets.forEach((checkbox) => {
      if (checkbox.value === facilityId) checkbox.checked = checked;
    });
    this.customizedValue = true;
    this._syncSelectAll();
    this._scheduleRecalculate();
  }

  onSelectAllChange(event) {
    const checked = event.target.checked;
    this.facilityCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked;
    });
    this.customizedValue = true;
    this._scheduleRecalculate();
  }

  onVolDeclaredInput(event) {
    toggleVolDeclaredEmptyState(event.target);
    this._scheduleRecalculate();
  }

  _scheduleRecalculate() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._recalculate(), 200);
  }

  async _recalculate() {
    if (!this.exchangeRatesUrlValue) return;
    if (this._fetchInFlight) {
      this._recalcQueued = true;
      return;
    }

    this._fetchInFlight = true;
    showModelingLoading();
    window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rates-loading"));

    let payload = null;
    let currencyDisplay = document.getElementById("scenario_currency_display")?.value || "payment";

    try {
      const url = new URL(this.exchangeRatesUrlValue, window.location.origin);
      const params = this._buildRequestParams();
      url.search = params.toString();

      const response = await fetch(url, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) throw new Error("facility selection request failed");

      payload = await response.json();
      currencyDisplay = document.getElementById("scenario_currency_display")?.value || "payment";
    } catch (_error) {
      window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rates-error"));
    } finally {
      this._fetchInFlight = false;
      hideModelingLoading();
      window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rates-ready"));
    }

    if (payload) {
      dispatchModelingPayload(payload, { currencyDisplay });

      window.dispatchEvent(
        new CustomEvent("scenario-modeling:exchange-rates-loaded", {
          detail: { payload, currencyDisplay },
        }),
      );
    }

    if (this._recalcQueued) {
      this._recalcQueued = false;
      this._scheduleRecalculate();
    }
  }

  _buildRequestParams() {
    const params = new URLSearchParams();
    appendModelingControlParams(params);
    return params;
  }

  _syncSelectAll() {
    if (!this.hasSelectAllCheckboxTarget) return;

    const total = this.facilityCheckboxTargets.length;
    const checked = this.facilityCheckboxTargets.filter((checkbox) => checkbox.checked).length;

    this.selectAllCheckboxTarget.checked = total > 0 && checked === total;
    this.selectAllCheckboxTarget.indeterminate = checked > 0 && checked < total;

    if (this.hasSelectedCountBadgeTarget) {
      this.selectedCountBadgeTarget.textContent = this.selectedCountTemplateValue.replace(
        "%{count}",
        String(checked)
      );
    }
  }
}
